import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type {
  Brand,
  CreateBrandParams,
  UpdateBrandParams,
  ScrapeBrandParams,
  DeleteBrandParams,
} from "../interfaces";

interface IBrandingService {
  createBrand(params: CreateBrandParams): Promise<Brand>;
  updateBrand(params: UpdateBrandParams): Promise<Brand>;
  deleteBrand(params: DeleteBrandParams): Promise<{ message: string; id: number }>;
  scrapeBrand(params: ScrapeBrandParams): Promise<Brand>;
  getAllBrands(): Promise<Brand[]>;
}

class BrandingService implements IBrandingService {
  private base = "/v1/branding";

  createBrand = async (params: CreateBrandParams): Promise<Brand> => {
    const formData = new FormData();

    formData.append("title", params.title);
    formData.append("description", params.description);

    // Handle image files
    if (params.images && params.images.length > 0) {
      params.images.forEach((image) => {
        formData.append("images", image);
      });
    }

    // Handle image URLs (alternative to file uploads)
    if (params.imageUrls && params.imageUrls.length > 0) {
      formData.append("imageUrls", JSON.stringify(params.imageUrls));
    }

    // Handle videos: either file uploads (up to 5) or URLs, not both
    if (params.videoFiles && params.videoFiles.length > 0) {
      params.videoFiles.forEach((video) => {
        formData.append("videos", video);
      });
    } else if (params.videos && params.videos.length > 0) {
      formData.append("videos", JSON.stringify(params.videos));
    }

    // Handle config
    if (params.config) {
      formData.append("config", JSON.stringify(params.config));
    }

    const res = await httpClient.post<ApiResponse<Brand>>(this.base, formData);

    return res.data.data;
  };

  updateBrand = async (params: UpdateBrandParams): Promise<Brand> => {
    const formData = new FormData();

    formData.append("id", params.id.toString());

    if (params.title) {
      formData.append("title", params.title);
    }

    if (params.description) {
      formData.append("description", params.description);
    }

    // Handle image files
    if (params.images && params.images.length > 0) {
      params.images.forEach((image) => {
        formData.append("images", image);
      });
    }

    // Handle image URLs (alternative to file uploads)
    if (params.imageUrls && params.imageUrls.length > 0) {
      formData.append("imageUrls", JSON.stringify(params.imageUrls));
    }

    // Handle videos: either file uploads (up to 5) or URLs
    if (params.videoFiles && params.videoFiles.length > 0) {
      params.videoFiles.forEach((video) => {
        formData.append("videos", video);
      });
    } else if (params.videos && params.videos.length > 0) {
      formData.append("videos", JSON.stringify(params.videos));
    }

    // Handle config
    if (params.config) {
      formData.append("config", JSON.stringify(params.config));
    }

    const res = await httpClient.put<ApiResponse<Brand>>(this.base, formData);

    return res.data.data;
  };

  deleteBrand = async (
    params: DeleteBrandParams
  ): Promise<{ message: string; id: number }> => {
    const res = await httpClient.delete<
      ApiResponse<{ message: string; id: number }>
    >(this.base, {
      data: { id: params.id },
    });

    return res.data.data;
  };

  scrapeBrand = async (params: ScrapeBrandParams): Promise<Brand> => {
    const res = await httpClient.post<ApiResponse<Brand>>(
      `${this.base}/scrape`,
      {
        url: params.url,
        ...(params.provider && { provider: params.provider }),
      }
    );

    return res.data.data;
  };

  getAllBrands = async (): Promise<Brand[]> => {
    const res = await httpClient.get<ApiResponse<Brand[]>>(this.base);

    return res.data.data;
  };
}

export const brandingService = new BrandingService();
